// Password caching utility - caches the staff password for 2 hours
// This prevents users from having to re-enter the password repeatedly

const CACHE_KEY = 'yyz-cbp-staff-password'
const CACHE_EXPIRY_KEY = 'yyz-cbp-staff-password-expiry'
const CACHE_DURATION_MS = 2 * 60 * 60 * 1000 // 2 hours in milliseconds

export const passwordCache = {
  /**
   * Get the cached password if it exists and hasn't expired
   * @returns The cached password or null if not found/expired
   */
  get(): string | null {
    try {
      const expiry = localStorage.getItem(CACHE_EXPIRY_KEY)
      if (!expiry) return null

      const expiryTime = parseInt(expiry, 10)
      if (Date.now() > expiryTime) {
        // Password has expired, clear it
        this.clear()
        return null
      }

      return localStorage.getItem(CACHE_KEY)
    } catch (error) {
      console.error('Error reading password cache:', error)
      return null
    }
  },

  /**
   * Store a password in the cache with 2-hour expiration
   * @param password The password to cache
   */
  set(password: string): void {
    try {
      const expiryTime = Date.now() + CACHE_DURATION_MS
      localStorage.setItem(CACHE_KEY, password)
      localStorage.setItem(CACHE_EXPIRY_KEY, expiryTime.toString())
    } catch (error) {
      console.error('Error setting password cache:', error)
    }
  },

  /**
   * Clear the cached password
   */
  clear(): void {
    try {
      localStorage.removeItem(CACHE_KEY)
      localStorage.removeItem(CACHE_EXPIRY_KEY)
    } catch (error) {
      console.error('Error clearing password cache:', error)
    }
  },

  /**
   * Check if there is a valid cached password
   * @returns true if a valid cached password exists
   */
  hasValidCache(): boolean {
    return this.get() !== null
  },

  /**
   * Get the remaining time until the password expires (in minutes)
   * @returns Minutes remaining or 0 if no valid cache
   */
  getRemainingMinutes(): number {
    try {
      const expiry = localStorage.getItem(CACHE_EXPIRY_KEY)
      if (!expiry) return 0

      const expiryTime = parseInt(expiry, 10)
      const remaining = expiryTime - Date.now()
      if (remaining <= 0) return 0

      return Math.ceil(remaining / (60 * 1000))
    } catch (error) {
      return 0
    }
  }
}
